<script setup lang="ts">
import CaptionButton from "../buttons/CaptionButton.vue";
import FullscreenButton from "../buttons/FullscreenButton.vue";
import MuteButton from "../buttons/MuteButton.vue";
// import PIPButton from "../buttons/PIPButton.vue";
import PlayButton from "../buttons/PlayButton.vue";
const RewindButton = defineAsyncComponent(() => 
  import("../buttons/RewindButton.vue")
);
const FastForwardButton = defineAsyncComponent(() => 
  import("../buttons/FastForwardButton.vue")
);
const ParticipateButton = defineAsyncComponent(() => 
  import("../buttons/ParticipateButton.vue")
);
import Captions from "../Captions.vue";
import ChapterTitle from "../ChapterTitle.vue";
// import Gestures from "../Gestures.vue";
import SettingsMenu from "../menus/SettingsMenu.vue";
import CaptionsMenu from "../menus/CaptionsMenu.vue";
import ChaptersMenu from "../menus/ChaptersMenu.vue";
import TimeSlider from "../sliders/TimeSlider.vue";
import VolumeSlider from "../sliders/VolumeSlider.vue";
import TimeGroup from "../TimeGroup.vue";
import CustomTime from "../CustomTime.vue";
import GenieButton from "../buttons/GenieButton.vue";

import { PlayerStore } from '@/front/stores/player_store'
const playerStore = PlayerStore();

import { VideoStore } from '@/front/stores/video_store'
const videoStore = VideoStore();

import { useGlobalRenderTracker } from '@/front/composables/GlobalRenderTracker.js'
if (playerStore.isDebugPerformance) {
  useGlobalRenderTracker('VideoLayout', { logLevel: 'none' })
}

// Import RelativeChapterTime only when needed
const RelativeChapterTime = defineAsyncComponent(() => 
  import("../RelativeChapterTime.vue")
);

const { thumbnails, controls, chapters, isClip, useChapterTiming } = defineProps<{
  thumbnails?: string;
  controls?: boolean;
  chapters?: boolean;
  isClip?: boolean;
  useChapterTiming?: boolean;
}>();

import { BrowserHelper } from '@/front/composables/BrowserHelper.js'
import { ArrowLeft02Icon, Backward02Icon } from "hugeicons-vue";
const { browserInfo } = BrowserHelper();

const getChapterClasses = computed(() => {
  return isClip || useChapterTiming ? 'opacity-0' : ''
})

const showTime = computed(() => {
  if (videoStore.video?.id && videoStore.video.design) {
    return videoStore.video.manage || videoStore.video.design.show_time
  }
  return true
})

const showSpeed = computed(() => {
  if (videoStore.video?.id && videoStore.video.design) {
    return videoStore.video.design.show_speed
  }
  return true
})

const hasCaptions = computed(() => {
  if (isClip) {
    return false
  }
  if (videoStore.video?.id && videoStore.video.subtitles.length > 0) {
    return true
  }
  return false
})

const hasQualities = computed(() => {
  try {
    if (!playerStore) {
      return false
    } 
    if (!playerStore.player) {
      return false
    }
    if (!playerStore.player.qualities) {
      return false
    }
    return playerStore.player.qualities.length > 1
  } catch (error) {
    // log.info(1,"Error in hasQualities", error)
    return false
  }
})

const canFullscreen = computed(() => {
  if (isClip) {
    return false
  }
  // Detect iOS devices
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  
  // iOS doesn't support fullscreen API (except for videos)
  if (isIOS) return false;
  
  // Check for standard fullscreen API support
  return !!(
    document.fullscreenEnabled ||
    document.webkitFullscreenEnabled ||
    document.mozFullScreenEnabled ||
    document.msFullscreenEnabled
  );
})

const showSettings = computed(() => {
  if (isClip) {
    return false
  }
  return showSpeed.value || hasQualities.value
})

const showRewind = computed(() => {
  if (isClip) {
    return false
  }
  return videoStore.video.design.show_rewind
})

const showFastForward = computed(() => {
  if (isClip) {
    return false
  }
  return videoStore.video.design.show_fastforward
})

const showPlayButton = computed(() => {
  if (controls) {
    return true
  }
  if (isClip) {
    return false
  }
  return videoStore.video.manage || !videoStore.video.block_pausing
})

const showParticipateButton = computed(() => {
  let result = !isClip && videoStore.video.participate && !videoStore.video.manage
  log.info(5, 'showParticipateButton', "clip: " + isClip + " participate: " + videoStore.video.participate + " manage: " + videoStore.video.manage, "result: " + result)
  if (isClip) {
    return false
  }
  return result
})

const showGenieButton = computed(() => {
  if (isClip) {
    return false
  }
  return videoStore.video.design.show_genie && videoStore.video.transcript && videoStore.video.transcript.sentences?.length > 0
})

const showChapters = computed(() => {
  if (isClip) {
    return false
  }
  if (!thumbnails) {
    return false
  }
  if (!chapters || chapters.length === 0) {
    return false
  }

  if (!videoStore.video.design.show_seekbar) {
    return false
  }

  if (!videoStore.video.design.seekbar_active) {
    return false
  }

  if (!videoStore.video.design.show_markers) {
    return false
  }

  if (videoStore.video.design.hide_controls) {
    return false
  }

  return true
})

import Spinner from '@/front/components/Spinner.vue';

</script>

<template>
  <!-- Handles doubleclick etc -->
  <!-- <Gestures /> -->
  <Captions />
  <media-controls
    id="media-controls"
    class="media-controls:opacity-100 pointer-events-none media-controls:pointer-events-auto absolute bottom-0 h-fit left-0 w-full z-[9] flex w-full flex-col opacity-0 transition-opacity duration-300"
  >
    <div class="flex-1" />
    <div class="bg-gradient-to-t from-black/40 to-black/0 pt-4">
      <media-controls-group class="flex w-full items-center px-2">
        <TimeSlider :thumbnails="thumbnails" />
      </media-controls-group>
      <media-controls-group class="flex w-full items-center px-2 pb-2">
        <Spinner v-if="playerStore.isWaiting" class="w-8 h-8 @lg/player:w-10 @lg/player:h-10 @2xl/player:w-12 @2xl/player:h-12 p-2"/>
        <PlayButton v-else-if="showPlayButton" tooltip-placement="top start" />
        <MuteButton v-if="!isClip" tooltip-placement="top" />
        <VolumeSlider class="hidden @xs/player:inline-flex"/>

        <RelativeChapterTime v-if="useChapterTiming && showTime && !playerStore.isDebug" />
        <CustomTime v-else-if="!isClip && showTime" />
      
        <ChapterTitle :class="getChapterClasses" class="opacity-0 @xs/player:opacity-100" />
        <ParticipateButton v-if="showParticipateButton" tooltip-placement="top" />
        <ChaptersMenu
          v-if="showChapters"
          placement="top end"
          tooltip-placement="top"
          :thumbnails="thumbnails"
        />
        <GenieButton v-if="showGenieButton" tooltip-placement="top" />

        <RewindButton v-if="showRewind" tooltip-placement="top"/>        <!-- <CaptionButton tooltip-placement="top" /> -->
        <FastForwardButton v-if="showFastForward" tooltip-placement="top"/>    
        <CaptionsMenu
          v-if="hasCaptions"
          placement="top end"
          tooltip-placement="top"
        /> 
        <SettingsMenu
          v-if="showSettings"
          :show-speed="showSpeed"
          :has-captions="hasCaptions"
          :has-qualities="hasQualities"
          placement="top end"
          tooltip-placement="top"
        />
        <!-- <PIPButton tooltip-placement="top" /> -->
        <FullscreenButton v-if="!isClip && canFullscreen" tooltip-placement="top end" />
      </media-controls-group>
    </div>
  </media-controls>
</template>

<style scoped>
media-controls {
  /* These CSS variables are supported out of the box to easily apply offsets to all tooltips/menus.  */
  --media-tooltip-y-offset: 30px;
  --media-menu-y-offset: 30px;
}

media-controls :deep(media-volume-slider) {
  --media-slider-preview-offset: 30px;
}
</style>
